import type { Command, KeyBinding } from "@codemirror/view";
import { EditorSelection } from "@codemirror/state";
import { insertNewlineContinueMarkup, deleteMarkupBackward } from "@codemirror/lang-markdown";

/**
 * Toggle an inline wrapper (e.g. ** for bold) around each selection range.
 * If the range is already wrapped, unwrap it. Empty selection wraps the
 * word under the cursor, or inserts an empty pair with the cursor inside.
 */
export function toggleWrap(marker: string): Command {
  return (view) => {
    const { state } = view;
    const changes = state.changeByRange((range) => {
      let { from, to } = range;

      // Empty selection: expand to word
      if (from === to) {
        const word = state.wordAt(from);
        if (word) {
          from = word.from;
          to = word.to;
        }
      }

      const before = state.sliceDoc(Math.max(0, from - marker.length), from);
      const after = state.sliceDoc(to, Math.min(state.doc.length, to + marker.length));
      const inner = state.sliceDoc(from, to);

      // Case 1: selection is wrapped outside -> unwrap
      if (before === marker && after === marker) {
        return {
          changes: [
            { from: from - marker.length, to: from, insert: "" },
            { from: to, to: to + marker.length, insert: "" },
          ],
          range: EditorSelection.range(from - marker.length, to - marker.length),
        };
      }
      // Case 2: selection includes the markers -> unwrap
      if (inner.startsWith(marker) && inner.endsWith(marker) && inner.length >= marker.length * 2) {
        return {
          changes: [
            { from, to: from + marker.length, insert: "" },
            { from: to - marker.length, to, insert: "" },
          ],
          range: EditorSelection.range(from, to - marker.length * 2),
        };
      }
      // Case 3: wrap
      return {
        changes: [
          { from, insert: marker },
          { from: to, insert: marker },
        ],
        range: EditorSelection.range(from + marker.length, to + marker.length),
      };
    });
    view.dispatch(changes, { scrollIntoView: true, userEvent: "input" });
    return true;
  };
}

/** Insert or toggle a [text](url) link around the selection. */
export const insertLink: Command = (view) => {
  const { state } = view;
  const changes = state.changeByRange((range) => {
    const text = state.sliceDoc(range.from, range.to);
    const insert = `[${text}](url)`;
    const urlStart = range.from + text.length + 3;
    return {
      changes: { from: range.from, to: range.to, insert },
      range: EditorSelection.range(urlStart, urlStart + 3),
    };
  });
  view.dispatch(changes, { scrollIntoView: true, userEvent: "input" });
  return true;
};

/** Set the current line's heading level exactly; 0 clears it. */
export function setHeading(level: number): Command {
  return (view) => {
    const { state } = view;
    const changes = state.changeByRange((range) => {
      const line = state.doc.lineAt(range.head);
      const m = /^(#{1,6})\s/.exec(line.text);
      const removed = m ? m[0].length : 0;
      const insert = level > 0 ? "#".repeat(level) + " " : "";
      return {
        changes: { from: line.from, to: line.from + removed, insert },
        range: EditorSelection.cursor(Math.max(line.from, range.head - removed + insert.length)),
      };
    });
    view.dispatch(changes, { userEvent: "input" });
    return true;
  };
}

/** Cycle the current line's heading level: none -> # -> ## -> ... -> ###### -> none */
const cycleHeading: Command = (view) => {
  const { state } = view;
  const changes = state.changeByRange((range) => {
    const line = state.doc.lineAt(range.head);
    const m = /^(#{1,6})\s/.exec(line.text);
    let insert: string;
    let removed = 0;
    if (!m) {
      insert = "# ";
    } else if (m[1]!.length >= 6) {
      insert = "";
      removed = m[0].length;
    } else {
      insert = "#";
    }
    if (removed > 0) {
      return {
        changes: { from: line.from, to: line.from + removed, insert: "" },
        range: EditorSelection.cursor(Math.max(line.from, range.head - removed)),
      };
    }
    return {
      changes: { from: line.from, insert },
      range: EditorSelection.cursor(range.head + insert.length),
    };
  });
  view.dispatch(changes, { userEvent: "input" });
  return true;
};

/**
 * Toggle a line prefix (lists, quotes) across all selected lines. If every
 * selected line already has it, remove it; otherwise add it.
 */
export function toggleLinePrefix(kind: "bullet" | "numbered" | "task" | "quote"): Command {
  const patterns: Record<string, RegExp> = {
    bullet: /^(\s*)- (?!\[)/,
    numbered: /^(\s*)\d+\. /,
    task: /^(\s*)- \[[ xX]\] /,
    quote: /^(\s*)> /,
  };
  const re = patterns[kind]!;
  return (view) => {
    const { state } = view;
    const range = state.selection.main;
    const startLine = state.doc.lineAt(range.from).number;
    const endLine = state.doc.lineAt(range.to).number;
    const lines = [];
    for (let n = startLine; n <= endLine; n++) lines.push(state.doc.line(n));

    const allHave = lines.every((l) => re.test(l.text) || l.text.trim() === "");
    const changes: { from: number; to?: number; insert?: string }[] = [];
    let counter = 1;
    for (const line of lines) {
      if (allHave) {
        const m = re.exec(line.text);
        if (m) changes.push({ from: line.from, to: line.from + m[0].length });
      } else if (!re.test(line.text)) {
        const stripped = line.text.replace(/^(\s*)(?:- \[[ xX]\] |- |\d+\. |> )/, "$1");
        const prefix =
          kind === "bullet" ? "- " : kind === "task" ? "- [ ] " : kind === "quote" ? "> " : `${counter}. `;
        changes.push({ from: line.from, to: line.to, insert: prefix + stripped });
      }
      counter++;
    }
    if (changes.length === 0) return true;
    view.dispatch({ changes, userEvent: "input" });
    return true;
  };
}

/** Insert a block snippet on its own line; $ marks the final cursor spot. */
export function insertBlock(snippet: string): Command {
  return (view) => {
    const { state } = view;
    const line = state.doc.lineAt(state.selection.main.head);
    const lineEmpty = line.text.trim() === "";
    const at = lineEmpty ? line.from : line.to;
    const to = lineEmpty ? line.to : at;
    const prefix = lineEmpty ? "" : "\n";
    const cursorIdx = snippet.indexOf("$");
    const body = snippet.replace("$", "");
    const cursor = at + prefix.length + (cursorIdx === -1 ? body.length : cursorIdx);
    view.dispatch({
      changes: { from: at, to, insert: prefix + body },
      selection: EditorSelection.cursor(cursor),
      scrollIntoView: true,
      userEvent: "input",
    });
    return true;
  };
}

export const markdownKeymap: readonly KeyBinding[] = [
  { key: "Enter", run: insertNewlineContinueMarkup },
  { key: "Backspace", run: deleteMarkupBackward },
  { key: "Mod-b", run: toggleWrap("**") },
  { key: "Mod-i", run: toggleWrap("*") },
  { key: "Mod-e", run: toggleWrap("`") },
  { key: "Mod-Shift-x", run: toggleWrap("~~") },
  { key: "Mod-Shift-h", run: toggleWrap("==") },
  { key: "Mod-k", run: insertLink },
  { key: "Mod-Shift-1", run: cycleHeading },
];
